/**
 * Minimal timestamp interface — compatible with `firebase.firestore.Timestamp`
 * without importing firebase directly.
 */
export interface TimestampLike {
  toMillis(): number;
}

interface AssignmentDateRange<T extends TimestampLike> {
  assignmentStartDate: T;
  assignmentEndDate: T | null;
}

/**
 * Computes the minimum allowed start date for a new generic device assignment.
 *
 * Backward assignments are not allowed: the start date must be at or after the
 * latest existing assignment boundary. For closed assignments the boundary is
 * `assignmentEndDate`; for an open assignment it is `assignmentStartDate`.
 *
 * @param projectStartDate  The project's start date (absolute lower bound).
 * @param existingAssignments  All existing assignments for the device (excluding the assignment being created/corrected).
 * @param knownSince  Optional device knownSince timestamp.
 * @returns The earliest allowed start date (same type as the inputs).
 */
export function computeMinDateForGenericAssignment<T extends TimestampLike>(
  projectStartDate: T,
  existingAssignments: AssignmentDateRange<T>[],
  knownSince?: T | null,
): T {
  let minDate = projectStartDate;

  if (knownSince && knownSince.toMillis() > minDate.toMillis()) {
    minDate = knownSince;
  }

  for (const existing of existingAssignments) {
    const boundary = existing.assignmentEndDate ?? existing.assignmentStartDate;
    if (boundary && boundary.toMillis() > minDate.toMillis()) {
      minDate = boundary;
    }
  }

  return minDate;
}

/**
 * Whether a project shows a generic device assignment.
 *
 * With "Alle Geräte verwalten" on, every assignment. With it off, only the open ones: an open
 * generic assignment still blocks concluding, archiving and deleting the project (here and in
 * IRES Cloud), so it has to stay visible until it is released. Who may release it is the usual
 * assignment rule (`canEditAssignments`): for a client working its projects in IRES Project, that
 * is the app. Closed ones stay hidden, as before (decided by Taimoor, 2026-10-19).
 *
 * @param assignment - the generic assignment; only its end date is read.
 * @param hasAllDevicesManagementAccess - whether the client has "Alle Geräte verwalten" on.
 */
export function isGenericDeviceAssignmentShown(
  assignment: { assignmentEndDate: TimestampLike | null | undefined },
  hasAllDevicesManagementAccess: boolean,
): boolean {
  return hasAllDevicesManagementAccess || !assignment.assignmentEndDate;
}
